/**
 * Small UI helpers: className combiner, clock formatting, phase labels, and the tier → Tailwind
 * style map. Tier colour is ALWAYS paired with icon + label in the UI (see TierBadge) so it
 * never relies on colour alone (WCAG 1.4.1).
 */
import { TIER_META, type OptionTier, type Phase } from "@patc/content-schema";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** m:ss from milliseconds (never negative). */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const PHASE_LABEL: Record<Phase, string> = {
  signing: "Signing",
  closing: "Closing",
  post_closing: "Post-closing",
};

export interface TierStyle {
  text: string;
  border: string;
  bgTint: string;
  bar: string;
}

/** Arbitrary-value Tailwind classes bound to the @theme --color-tier-* variables (see index.css). */
export const TIER_STYLE: Record<OptionTier, TierStyle> = {
  best: {
    text: "text-[var(--color-tier-best)]",
    border: "border-[var(--color-tier-best)]",
    bgTint: "bg-[color-mix(in_oklab,var(--color-tier-best)_9%,var(--color-card))]",
    bar: "bg-[var(--color-tier-best)]",
  },
  good: {
    text: "text-[var(--color-tier-good)]",
    border: "border-[var(--color-tier-good)]",
    bgTint: "bg-[color-mix(in_oklab,var(--color-tier-good)_10%,var(--color-card))]",
    bar: "bg-[var(--color-tier-good)]",
  },
  weak: {
    text: "text-[var(--color-tier-weak)]",
    border: "border-[var(--color-tier-weak)]",
    bgTint: "bg-[color-mix(in_oklab,var(--color-tier-weak)_10%,var(--color-card))]",
    bar: "bg-[var(--color-tier-weak)]",
  },
  worst: {
    text: "text-[var(--color-tier-worst)]",
    border: "border-[var(--color-tier-worst)]",
    bgTint: "bg-[color-mix(in_oklab,var(--color-tier-worst)_12%,var(--color-card))]",
    bar: "bg-[var(--color-tier-worst)]",
  },
};

export { TIER_META };
